import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, FilePlus2, LogOut, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

function initials(email = "") {
    const local = email.split("@")[0] || "";
    const parts = local.split(/[._-]/).filter(Boolean);
    return (parts.length > 1 ? parts[0][0] + parts[1][0] : local.slice(0, 2)).toUpperCase();
}

const navItems = [
    {
        to: "/dashboard",
        label: "Tableau de bord",
        icon: LayoutDashboard,
        testId: "nav-dashboard",
    },
    {
        to: "/nouveau-dossier",
        label: "Nouveau Dossier",
        icon: FilePlus2,
        testId: "nav-new-dossier",
    },
    {
        to: "/prompts-ia",
        label: "Prompts IA",
        icon: Sparkles,
        testId: "nav-prompts-ia",
    },
];

export default function Sidebar() {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const email = user?.email ?? "";

    async function handleLogout() {
        const err = await signOut();
        if (!err) navigate("/login", { replace: true });
    }

    return (
        <aside
            data-testid="sidebar"
            className="w-64 shrink-0 bg-primary text-primary-foreground flex flex-col min-h-screen"
        >
            {/* Brand */}
            <div className="px-6 pt-8 pb-10">
                <img
                    data-testid="brand-logo"
                    src="/logos/logo-acs-blanc.webp"
                    alt="ACS Corporate"
                    className="h-10 w-auto -ml-2"
                />
                <p className="mt-3 text-xs uppercase tracking-[0.18em] text-white/40">
                    Audit &amp; Restructuration
                </p>
            </div>

            {/* Nav */}
            <nav
                data-testid="sidebar-nav"
                className="flex-1 px-3 flex flex-col gap-1"
            >
                {navItems.map(({ to, label, icon: Icon, testId }) => (
                    <NavLink
                        key={to}
                        to={to}
                        data-testid={testId}
                        className={({ isActive }) =>
                            cn(
                                "group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium",
                                "transition-colors duration-200",
                                isActive
                                    ? "bg-white/10 text-white"
                                    : "text-white/65 hover:text-white hover:bg-white/5"
                            )
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <span
                                    className={cn(
                                        "h-6 w-[2px] rounded-full transition-colors",
                                        isActive
                                            ? "bg-white"
                                            : "bg-transparent group-hover:bg-white/30"
                                    )}
                                />
                                <Icon
                                    className="h-4 w-4 shrink-0"
                                    strokeWidth={1.75}
                                />
                                <span>{label}</span>
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            {/* Footer */}
            <div className="px-6 py-6 border-t border-white/10">
                <div
                    data-testid="sidebar-user"
                    className="flex items-center gap-3"
                >
                    <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-semibold">
                        {initials(email)}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p data-testid="sidebar-user-email" className="text-sm font-medium truncate">
                            {email}
                        </p>
                        <p className="text-xs text-white/50 truncate">
                            Data Room
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="logout-button"
                        onClick={handleLogout}
                        title="Se déconnecter"
                        className="h-8 w-8 rounded-md flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        <LogOut className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                </div>
            </div>
        </aside>
    );
}
